import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Paper,
  CircularProgress,
} from "@mui/material";
import {
  useSpecificLocationsData,
  useRefreshTimer,
  useBilletCount,
} from "../../hooks";
import { Alert } from "@mui/material";
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { getLocationDisplayText } from "../../utils/locationDisplay";
import {
  LocationSection,
  TouchKeyboard,
  LabDataSection,
} from "./move-billets-modal/components";
import {
  moveBillets,
  getLabData,
} from "../../repository/BilletYardInventoryRepository";
import { PhysicsData, ChemistryData } from "../../models/coreModels";
import { useUTLSettingsContext } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { PERMISSIONS } from "../../hooks/core/constants";
import { handleApiResponse } from "../../utils/responseHandler";
import { getTotalBilletsCount } from "../../utils/getTotalBilletsCount";

interface MoveBilletsModalProps {
  show: boolean;
  onHide: () => void;
  sourceLocationId: number;
  destinationLocationId: number;
  selectedCraneId: number;
  refreshInterval?: number;
  canBypassBilletLimit: boolean;
}

export const MoveBilletsModal = ({
  show,
  onHide,
  sourceLocationId,
  destinationLocationId,
  selectedCraneId,
  refreshInterval = 5,
  canBypassBilletLimit,
}: MoveBilletsModalProps) => {
  // Special location IDs that should bypass direction type validation
  // These locations need to allow moves in both directions for error correction
  const SPECIAL_LOCATION_IDS = [1, 2, 30, 31, 32, 100, 101]; // Cooling Bed, Homeless, Truck Loading, Back Porch, Bin, RailCar Loading, Scrap

  // States
  const [error, setError] = useState<string>("");
  const [isMoving, setIsMoving] = useState<boolean>(false);
  const [warningMessage, setWarningMessage] = useState<string>("");
  const [pendingMoveParams, setPendingMoveParams] = useState<{
    direction: "left-to-right" | "right-to-left";
  } | null>(null);

  const [selectedSourceGroupIndex, setSelectedSourceGroupIndex] = useState<
    number | null
  >(null);
  const [selectedDestinationGroupIndex, setSelectedDestinationGroupIndex] =
    useState<number | null>(null);

  // Lab data state for physics and chemistry display
  const [sourcePhysicsData, setSourcePhysicsData] = useState<PhysicsData[]>([]);
  const [destinationPhysicsData, setDestinationPhysicsData] = useState<
    PhysicsData[]
  >([]);
  const [sourceChemistryData, setSourceChemistryData] = useState<
    ChemistryData[]
  >([]);
  const [destinationChemistryData, setDestinationChemistryData] = useState<
    ChemistryData[]
  >([]);

  // Toggle state for showing/hiding tensile estimations per location (hidden by default)
  const [showSourceLabData, setShowSourceLabData] = useState<boolean>(false);
  const [showDestinationLabData, setShowDestinationLabData] =
    useState<boolean>(false);

  const { state } = useUTLSettingsContext();
  const operatorMaxBillets = parseInt(
    get(
      state.settings,
      "BYI_APP_OPERATOR_MAX_BILLETS",
      PERMISSIONS.OPERATOR_BILLET_MOVE_LIMIT,
    ),
  );

  // ------------------
  // Custom Hook calls
  // ------------------
  const { billetsCount, setBilletsCount, handleKeypadInput } = useBilletCount(
    () => setError(""),
  );

  const {
    isLoading: dataLoading,
    sourceLocation: modalSourceLocation,
    destinationLocation: modalDestinationLocation,
    sourceGroups,
    destinationGroups,
    hasError,
    errorMessage,
    hasBilletsToMove,
    refreshModalData,
    refreshBothLocationsData,
  } = useSpecificLocationsData({
    sourceLocationId,
    destinationLocationId,
  });

  useRefreshTimer(refreshInterval, refreshModalData);

  // --------------------------------
  // Display Billets by Group Name
  // --------------------------------
  // Convert modal data to SelectedLocationForMove format for compatibility
  const sourceLocation = useMemo(() => {
    if (!modalSourceLocation) return null;
    return {
      location: modalSourceLocation,
      groups: sourceGroups || [],
      selectedGroupIndex: selectedSourceGroupIndex,
      selectedBilletsCount: 1,
      role: "source" as const,
    };
  }, [modalSourceLocation, sourceGroups, selectedSourceGroupIndex]);

  const destinationLocation = useMemo(() => {
    if (!modalDestinationLocation) return null;
    return {
      location: modalDestinationLocation,
      groups: destinationGroups || [],
      selectedGroupIndex: selectedDestinationGroupIndex,
      selectedBilletsCount: 1,
      role: "destination" as const,
    };
  }, [
    modalDestinationLocation,
    destinationGroups,
    selectedDestinationGroupIndex,
  ]);

  // ------------------
  // Direct Parameter Calculation
  // ------------------

  const calculateMoveParameters = useCallback(
    (direction: "left-to-right" | "right-to-left") => {
      // 1. Determine actual source/destination based on direction
      const isLeftToRight = direction === "left-to-right";

      // 2. Get arrays and indexes based on direction
      const actualSourceGroups = isLeftToRight
        ? sourceGroups
        : destinationGroups;
      const actualDestinationGroups = isLeftToRight
        ? destinationGroups
        : sourceGroups;
      const actualSelectedIndex = isLeftToRight
        ? selectedSourceGroupIndex
        : selectedDestinationGroupIndex;

      // 3. Get selected group (auto-select first if none selected)
      const selectedGroup = actualSourceGroups[actualSelectedIndex ?? 0];

      // 4. Return all parameters directly
      // Note: positionDestiny is null to let SQL determine position based on AddFillingType
      // This enables implicit merging in AddBilletToFirstInputInternal/AddBilletToLastInputInternal
      return {
        locationContentIDOrigin: selectedGroup.LocationContentID,
        locationIDOrigin: isLeftToRight
          ? sourceLocationId
          : destinationLocationId,
        positionOrigin: selectedGroup.Position,
        locationContentIDDestiny: null,
        locationIDDestiny: isLeftToRight
          ? destinationLocationId
          : sourceLocationId,
        positionDestiny: null,
        billetsCount: billetsCount || selectedGroup.BilletsCount,
        craneID: selectedCraneId,
        checkValidation: true,
      };
    },
    [
      sourceGroups,
      destinationGroups,
      selectedSourceGroupIndex,
      selectedDestinationGroupIndex,
      sourceLocationId,
      destinationLocationId,
      billetsCount,
      selectedCraneId,
    ],
  );

  // ------------------
  // Execution Call
  // ------------------

  const handleMoveExecute = useCallback(
    async (
      direction: "left-to-right" | "right-to-left",
      bypassValidation = false,
    ) => {
      // Early validation
      if (isMoving) return;

      try {
        // Set loading state
        setIsMoving(true);
        setError("");
        setWarningMessage("");

        // Calculate parameters directly
        const params = calculateMoveParameters(direction);

        // Call repository directly - no intermediate layers!
        const response = await moveBillets(
          params.locationContentIDOrigin,
          params.locationIDOrigin,
          params.positionOrigin,
          params.locationContentIDDestiny,
          params.locationIDDestiny,
          params.positionDestiny,
          params.billetsCount,
          params.craneID,
          bypassValidation ? false : params.checkValidation, // Override checkValidation if bypassing
        );

        // Handle response using centralized handler
        const result = handleApiResponse(response);

        if (result.type === "error") {
          setError(result.message);
          return;
        }

        if (result.type === "warning" && !bypassValidation) {
          // Show warning confirmation dialog
          setWarningMessage(result.message);
          setPendingMoveParams({ direction });
          return;
        }

        // Success handling (A) or bypassed Warning (W)
        setBilletsCount(0);
        setError("");
        setWarningMessage("");
        setPendingMoveParams(null);

        // Refresh both locations in a single efficient call
        await refreshBothLocationsData();
      } catch (error: unknown) {
        setError(
          error instanceof Error ? error.message : "Failed to move billets",
        );
      } finally {
        setIsMoving(false);
      }
    },
    [
      isMoving,
      calculateMoveParameters,
      setBilletsCount,
      refreshBothLocationsData,
    ],
  );

  // ----------------------------------
  // Warning confirmation handlers
  // ----------------------------------

  const handleConfirmWarning = useCallback(async () => {
    if (!pendingMoveParams) return;

    // Retry the move with validation bypassed
    await handleMoveExecute(pendingMoveParams.direction, true);
  }, [pendingMoveParams, handleMoveExecute]);

  const handleCancelWarning = useCallback(() => {
    setWarningMessage("");
    setPendingMoveParams(null);
  }, []);

  // ----------------------------------
  // Auto-select first available group
  // ----------------------------------

  // Auto-select first available group when source groups change (only if no selection exists)
  useEffect(() => {
    if (sourceGroups && sourceGroups.length > 0) {
      setSelectedSourceGroupIndex((prevIndex) => {
        // Check if current selection is still valid
        if (prevIndex !== null) {
          const selectedGroup = sourceGroups[prevIndex];
          if (!selectedGroup) {
            // Selected group no longer exists, reset to first group (no IsSelected check)
            // Fetch lab data for the first group
            if (sourceGroups[0]?.HeatNumber) {
              fetchLabData(
                sourceGroups[0].HeatNumber,
                setSourcePhysicsData,
                setSourceChemistryData,
              );
            }
            return 0;
          }
          return prevIndex; // Keep current selection if group still exists
        } else {
          // No selection exists, auto-select first group (no IsSelected check)
          // Fetch lab data for the first group
          if (sourceGroups[0]?.HeatNumber) {
            fetchLabData(
              sourceGroups[0].HeatNumber,
              setSourcePhysicsData,
              setSourceChemistryData,
            );
          }
          return 0;
        }
      });
    } else {
      setSelectedSourceGroupIndex(null);
      setSourcePhysicsData([]);
      setSourceChemistryData([]);
    }
  }, [sourceGroups]);

  // Auto-select first available group when destination groups change (only if no selection exists)
  useEffect(() => {
    if (destinationGroups && destinationGroups.length > 0) {
      setSelectedDestinationGroupIndex((prevIndex) => {
        // Check if current selection is still valid
        if (prevIndex !== null) {
          const selectedGroup = destinationGroups[prevIndex];
          if (!selectedGroup) {
            // Selected group no longer exists, reset to first group (no IsSelected check)
            // Fetch lab data for the first group
            if (destinationGroups[0]?.HeatNumber) {
              fetchLabData(
                destinationGroups[0].HeatNumber,
                setDestinationPhysicsData,
                setDestinationChemistryData,
              );
            }
            return 0;
          }
          return prevIndex; // Keep current selection if group still exists
        } else {
          // No selection exists, auto-select first group (no IsSelected check)
          // Fetch lab data for the first group
          if (destinationGroups[0]?.HeatNumber) {
            fetchLabData(
              destinationGroups[0].HeatNumber,
              setDestinationPhysicsData,
              setDestinationChemistryData,
            );
          }
          return 0;
        }
      });
    } else {
      setSelectedDestinationGroupIndex(null);
      setDestinationPhysicsData([]);
      setDestinationChemistryData([]);
    }
  }, [destinationGroups]);

  // ------------------------
  // Selected Group Billet Counts
  // ------------------------

  // Get selected source group billet count
  const selectedSourceGroupBilletsCount = useMemo(() => {
    if (!sourceGroups || selectedSourceGroupIndex === null) return undefined;
    const selectedGroup = sourceGroups[selectedSourceGroupIndex];
    return selectedGroup?.BilletsCount;
  }, [sourceGroups, selectedSourceGroupIndex]);

  // Get selected destination group billet count
  const selectedDestinationGroupBilletsCount = useMemo(() => {
    if (!destinationGroups || selectedDestinationGroupIndex === null)
      return undefined;
    const selectedGroup = destinationGroups[selectedDestinationGroupIndex];
    return selectedGroup?.BilletsCount;
  }, [destinationGroups, selectedDestinationGroupIndex]);

  // Get selected heats for lab data display
  const selectedSourceHeat = useMemo(() => {
    if (!sourceGroups || selectedSourceGroupIndex === null) return null;
    return sourceGroups[selectedSourceGroupIndex] || sourceGroups[0] || null;
  }, [sourceGroups, selectedSourceGroupIndex]);

  const selectedDestinationHeat = useMemo(() => {
    if (!destinationGroups || selectedDestinationGroupIndex === null)
      return null;
    return (
      destinationGroups[selectedDestinationGroupIndex] ||
      destinationGroups[0] ||
      null
    );
  }, [destinationGroups, selectedDestinationGroupIndex]);

  // ------------------------
  // Total Billets Count for Locations
  // ------------------------

  const sourceTotalBillets = useMemo(
    () => getTotalBilletsCount(sourceGroups),
    [sourceGroups],
  );

  const destinationTotalBillets = useMemo(
    () => getTotalBilletsCount(destinationGroups),
    [destinationGroups],
  );

  // ------------------------
  // Lab Data Section Visibility
  // ------------------------
  // Show LabDataSection for BOTH locations if ANY location has ShowTensileEstimation=true
  const shouldShowLabDataSection = useMemo(() => {
    const sourceHasEstimation =
      modalSourceLocation?.ShowTensileEstimation === true;
    const destinationHasEstimation =
      modalDestinationLocation?.ShowTensileEstimation === true;
    return sourceHasEstimation || destinationHasEstimation;
  }, [
    modalSourceLocation?.ShowTensileEstimation,
    modalDestinationLocation?.ShowTensileEstimation,
  ]);

  // ------------------------
  // TouchKeyboard deps
  // ------------------------

  // Memoized disabled state calculations to prevent unnecessary re-renders
  const isRightToLeftDisabled = useMemo(() => {
    if (billetsCount === 0) return true;
    // Check operator billet limit
    if (!canBypassBilletLimit && billetsCount > operatorMaxBillets) return true;

    // Check if destination location has any billets
    if (!destinationGroups || destinationGroups.length === 0) return true;

    // Check location direction capabilities (skip for special locations)
    if (modalSourceLocation && modalDestinationLocation) {
      const isSourceSpecial = SPECIAL_LOCATION_IDS.includes(sourceLocationId);
      const isDestinationSpecial = SPECIAL_LOCATION_IDS.includes(
        destinationLocationId,
      );

      // Skip direction validation if either location is special (allows error correction)
      if (!isSourceSpecial && !isDestinationSpecial) {
        // For right-to-left: source (right) should be able to output, destination (left) should be able to input
        const sourceCanOutput =
          modalDestinationLocation.LocationDirectionType === "O" ||
          modalDestinationLocation.LocationDirectionType === "B";
        const destinationCanInput =
          modalSourceLocation.LocationDirectionType === "I" ||
          modalSourceLocation.LocationDirectionType === "B";

        if (!sourceCanOutput || !destinationCanInput) return true;
      }
    }

    // For right-to-left: Allow operator to enter more billets than shown for corrections
    // Backend validation will handle discrepancies and return warnings if needed
    // This matches old MES behavior where operators could correct count mismatches

    return false;
  }, [
    billetsCount,
    canBypassBilletLimit,
    destinationGroups,
    sourceLocationId,
    destinationLocationId,
    modalSourceLocation?.LocationDirectionType,
    modalDestinationLocation?.LocationDirectionType,
  ]);

  const isLeftToRightDisabled = useMemo(() => {
    if (billetsCount === 0) return true;
    // Check operator billet limit
    if (!canBypassBilletLimit && billetsCount > operatorMaxBillets) return true;

    // Check if source location has any billets
    if (!sourceGroups || sourceGroups.length === 0) return true;

    // Check location direction capabilities (skip for special locations)
    if (modalSourceLocation && modalDestinationLocation) {
      const isSourceSpecial = SPECIAL_LOCATION_IDS.includes(sourceLocationId);
      const isDestinationSpecial = SPECIAL_LOCATION_IDS.includes(
        destinationLocationId,
      );

      // Skip direction validation if either location is special (allows error correction)
      if (!isSourceSpecial && !isDestinationSpecial) {
        // For left-to-right: source (left) should be able to output, destination (right) should be able to input
        const sourceCanOutput =
          modalSourceLocation.LocationDirectionType === "O" ||
          modalSourceLocation.LocationDirectionType === "B";
        const destinationCanInput =
          modalDestinationLocation.LocationDirectionType === "I" ||
          modalDestinationLocation.LocationDirectionType === "B";

        if (!sourceCanOutput || !destinationCanInput) return true;
      }
    }

    // For left-to-right: Allow operator to enter more billets than shown for corrections
    // Backend validation will handle discrepancies and return warnings if needed
    // This matches old MES behavior where operators could correct count mismatches

    return false;
  }, [
    billetsCount,
    canBypassBilletLimit,
    sourceGroups,
    sourceLocationId,
    destinationLocationId,
    modalSourceLocation?.LocationDirectionType,
    modalDestinationLocation?.LocationDirectionType,
  ]);

  // Memoized move handlers to prevent TouchKeyboard re-renders
  const handleMoveLeftToRight = useCallback(
    () => handleMoveExecute("left-to-right"),
    [handleMoveExecute],
  );

  const handleMoveRightToLeft = useCallback(
    () => handleMoveExecute("right-to-left"),
    [handleMoveExecute],
  );

  // Fetch lab data (physics and chemistry) for a heat
  const fetchLabData = async (
    heatNumber: string,
    setPhysics: React.Dispatch<React.SetStateAction<PhysicsData[]>>,
    setChemistry: React.Dispatch<React.SetStateAction<ChemistryData[]>>,
  ) => {
    if (!heatNumber) {
      setPhysics([]);
      setChemistry([]);
      return;
    }
    const data = await getLabData(heatNumber);
    setPhysics(data.physics);
    setChemistry(data.chemistry);
  };

  // Handle group selection from source & destination locations
  // useCallback is needed because these handlers are passed to memoized LocationSection component
  const handleSourceGroupSelection = useCallback(
    (globalIndex: number) => {
      setSelectedSourceGroupIndex(globalIndex);
      setError("");
      // Fetch lab data for the selected group's heat
      const selectedGroup = sourceGroups?.[globalIndex];
      if (selectedGroup?.HeatNumber) {
        fetchLabData(
          selectedGroup.HeatNumber,
          setSourcePhysicsData,
          setSourceChemistryData,
        );
      }
    },
    [sourceGroups],
  );
  const handleDestinationGroupSelection = useCallback(
    (globalIndex: number) => {
      setSelectedDestinationGroupIndex(globalIndex);
      setError("");
      // Fetch lab data for the selected group's heat
      const selectedGroup = destinationGroups?.[globalIndex];
      if (selectedGroup?.HeatNumber) {
        fetchLabData(
          selectedGroup.HeatNumber,
          setDestinationPhysicsData,
          setDestinationChemistryData,
        );
      }
    },
    [destinationGroups],
  );

  // Memoized warning messages to prevent LocationSection re-renders
  const sourceWarningMessage = useMemo(() => {
    if (!sourceLocation) return undefined;
    const directionType = sourceLocation.location.LocationDirectionType;
    if (directionType === "B") return undefined; // BOTH - no warning
    if (directionType === "I") return "Location is not configured for output";
    if (directionType === "O") return "Location is not configured for input";
    if (directionType === "N")
      return "Location is not configured for input/output";
    return undefined;
  }, [sourceLocation?.location.LocationDirectionType]);

  const destinationWarningMessage = useMemo(() => {
    if (!destinationLocation) return undefined;
    const directionType = destinationLocation.location.LocationDirectionType;
    if (directionType === "B") return undefined; // BOTH - no warning
    if (directionType === "I") return "Location is not configured for output";
    if (directionType === "O") return "Location is not configured for input";
    if (directionType === "N")
      return "Location is not configured for input/output";
    return undefined;
  }, [destinationLocation?.location.LocationDirectionType]);

  // Get the display text for the source location
  const getSourceLocationTitle = () => {
    let locationText = "No Billets Available";

    if (sourceLocation) {
      locationText = getLocationDisplayText(sourceLocation.location);
    } else if (modalSourceLocation) {
      locationText = getLocationDisplayText(modalSourceLocation);
    }

    // Add total billet count if available
    if (sourceTotalBillets > 0) {
      return `${locationText} (${sourceTotalBillets})`;
    }

    return locationText;
  };

  // Get the display text for the destination location
  const getDestinationLocationTitle = () => {
    let locationText = "Select Destination";

    if (destinationLocation) {
      locationText = getLocationDisplayText(destinationLocation.location);
    } else if (modalDestinationLocation) {
      locationText = getLocationDisplayText(modalDestinationLocation);
    }

    // Add total billet count if available
    if (destinationTotalBillets > 0) {
      return `${locationText} (${destinationTotalBillets})`;
    }

    return locationText;
  };

  // -------------------------
  // Modal show/hide handlers
  // -------------------------

  const handleClose = (
    event: {},
    reason: "backdropClick" | "escapeKeyDown",
  ) => {
    if (
      isMoving &&
      (reason === "backdropClick" || reason === "escapeKeyDown")
    ) {
      return;
    }
    onHide();
  };

  // Clear errors when modal opens
  useEffect(() => {
    if (show) {
      setError("");
    }
  }, [show]);

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      fullScreen
      disableEscapeKeyDown={isMoving}
    >
      <DialogTitle
        sx={{
          backgroundColor: (theme) => theme.palette.primary.main,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          padding: (theme) => theme.spacing(1.5, 3),
        }}
      >
        <Typography
          variant="h5"
          component="div"
          sx={{
            fontWeight: "bold",
            color: (theme) => theme.palette.primary.contrastText,
            textAlign: "center",
            fontSize: "20px",
          }}
        >
          Move Billets
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          height: "calc(100vh - 160px)",
          paddingTop: "16px",
          paddingBottom: "16px",
          paddingLeft: "8px",
          paddingRight: "8px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {error && (
          <Alert
            severity="error"
            sx={{
              marginBottom: (theme) => theme.spacing(2),
              flexShrink: 0,
            }}
          >
            {error}
          </Alert>
        )}

        {/* Warning Confirmation Dialog */}
        <Dialog
          open={!!warningMessage}
          onClose={handleCancelWarning}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center">
              <WarningIcon
                sx={{
                  color: (theme) => theme.palette.warning.main,
                  marginRight: "0.5rem",
                  fontSize: "2rem",
                }}
              />
              <Typography variant="h5">Warning</Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ paddingTop: "1rem", paddingBottom: "1.5rem" }}>
            <Typography variant="h6" sx={{ marginBottom: "1rem" }}>
              {warningMessage}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Do you want to continue?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ padding: "1rem 1.5rem", gap: "1rem" }}>
            <Button
              onClick={handleCancelWarning}
              color="inherit"
              variant="outlined"
              size="large"
              sx={{
                minWidth: "150px",
                minHeight: "56px",
                fontSize: "1.1rem",
                flex: 1,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmWarning}
              color="primary"
              variant="contained"
              size="large"
              autoFocus
              sx={{
                minWidth: "150px",
                minHeight: "56px",
                fontSize: "1.1rem",
                flex: 1,
              }}
            >
              Continue
            </Button>
          </DialogActions>
        </Dialog>

        {/* Loading State */}
        {dataLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "2rem",
              flex: 1,
            }}
          >
            <CircularProgress />
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ marginTop: "1rem" }}
            >
              Loading location data...
            </Typography>
          </Box>
        ) : hasError ? (
          /* Error State */
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "2rem",
              flex: 1,
            }}
          >
            <ErrorIcon color="error" sx={{ fontSize: 48 }} />
            <Typography variant="h6" color="error" sx={{ marginTop: "1rem" }}>
              Error Loading Data
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {errorMessage}
            </Typography>
            <Button variant="outlined" sx={{ marginTop: "1rem" }}>
              Retry
            </Button>
          </Box>
        ) : !hasBilletsToMove ? (
          /* Empty State - No Billets to Move */
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "2rem",
              flex: 1,
            }}
          >
            <WarningIcon
              sx={{
                fontSize: 48,
                color: (theme) => theme.palette.warning.main,
              }}
            />
            <Typography
              variant="h6"
              color="textPrimary"
              sx={{ marginTop: "1rem" }}
            >
              No Billets to Move
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ textAlign: "center" }}
            >
              Both locations are empty. There are no billets to move between
              these locations.
            </Typography>
            <Box sx={{ marginTop: "1rem" }}>
              <Typography variant="body2" color="textSecondary">
                Source: {modalSourceLocation?.HeaderText || "Unknown"}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Destination: {modalDestinationLocation?.HeaderText || "Unknown"}
              </Typography>
            </Box>
          </Box>
        ) : (
          /* Normal Move Interface */
          <Box
            sx={{
              opacity: isMoving ? 0.5 : 1,
              pointerEvents: isMoving ? "none" : "auto",
              transition: "opacity 0.3s ease",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              position: "relative",
              overflow: "hidden",
              minHeight: 0, // Allow flex children to shrink
            }}
          >
            {/* Material UI Grid Three-Column Layout */}
            <Grid
              container
              spacing={1}
              sx={{
                flex: 1,
                minHeight: 0,
                marginTop: "8px",
              }}
            >
              {/* Left Column - Source Location */}

              <Grid
                item
                xs={4}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "auto",
                }}
              >
                <Box
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                    padding: "6px",
                    borderRadius: "5px 5px 0 0",
                    textAlign: "center",
                    fontWeight: "bold",
                    fontSize: "16px",
                    flexShrink: 0,
                    marginBottom: "16px",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "16px",
                      fontWeight: "bold",
                      color: "white",
                      margin: 0,
                    }}
                  >
                    {getSourceLocationTitle()}
                  </Typography>
                </Box>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    background: (theme) =>
                      `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: (theme) => theme.shape.borderRadius,
                    padding: (theme) => theme.spacing(1),
                    boxShadow: (theme) => theme.shadows[2],
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    overflow: "auto",
                  }}
                >
                  <LocationSection
                    location={sourceLocation}
                    fallbackLocation={modalSourceLocation}
                    getLocationDisplayText={getLocationDisplayText}
                    onGroupSelect={handleSourceGroupSelection}
                    warningMessage={sourceWarningMessage}
                  />
                </Paper>

                {/* Source Lab Data Section - toggled by button */}
                {shouldShowLabDataSection && showSourceLabData ? (
                  <LabDataSection
                    heat={selectedSourceHeat}
                    physicsData={sourcePhysicsData}
                    chemistryData={sourceChemistryData}
                    comment={selectedSourceHeat?.Comments ?? null}
                  />
                ) : null}
              </Grid>

              {/* Middle Column - TouchKeyboard */}
              <Grid
                item
                xs={4}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <TouchKeyboard
                  billetsCount={billetsCount}
                  onKeypadInput={handleKeypadInput}
                  onMoveLeftToRight={handleMoveLeftToRight}
                  onMoveRightToLeft={handleMoveRightToLeft}
                  isLeftToRightDisabled={isLeftToRightDisabled}
                  isRightToLeftDisabled={isRightToLeftDisabled}
                  selectedSourceGroupBilletsCount={
                    selectedSourceGroupBilletsCount
                  }
                  selectedDestinationGroupBilletsCount={
                    selectedDestinationGroupBilletsCount
                  }
                />
              </Grid>

              {/* Right Column - Destination Location */}
              <Grid
                item
                xs={4}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "auto",
                }}
              >
                <Box
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                    padding: "6px",
                    borderRadius: "5px 5px 0 0",
                    textAlign: "center",
                    fontWeight: "bold",
                    fontSize: "16px",
                    flexShrink: 0,
                    marginBottom: "16px",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "16px",
                      fontWeight: "bold",
                      color: "white",
                      margin: 0,
                    }}
                  >
                    {getDestinationLocationTitle()}
                  </Typography>
                </Box>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    background: (theme) =>
                      `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: (theme) => theme.shape.borderRadius,
                    padding: (theme) => theme.spacing(1),
                    boxShadow: (theme) => theme.shadows[2],
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    overflow: "auto",
                  }}
                >
                  <LocationSection
                    location={destinationLocation}
                    fallbackLocation={modalDestinationLocation}
                    getLocationDisplayText={getLocationDisplayText}
                    onGroupSelect={handleDestinationGroupSelection}
                    warningMessage={destinationWarningMessage}
                  />
                </Paper>

                {/* Destination Lab Data Section - toggled by button */}
                {shouldShowLabDataSection && showDestinationLabData ? (
                  <LabDataSection
                    heat={selectedDestinationHeat}
                    physicsData={destinationPhysicsData}
                    chemistryData={destinationChemistryData}
                    comment={selectedDestinationHeat?.Comments ?? null}
                  />
                ) : null}
              </Grid>
            </Grid>

            {/* Moving Loader Overlay */}
            {isMoving && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  zIndex: 1000,
                }}
              >
                <CircularProgress size={60} />
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ marginTop: "1rem" }}
                >
                  Moving billets...
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          padding: (theme) => theme.spacing(2),
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {/* Source Tensile Toggle Button - left side */}
        <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
          {shouldShowLabDataSection && (
            <Button
              onClick={() => setShowSourceLabData((prev) => !prev)}
              variant="outlined"
              size="large"
              startIcon={
                showSourceLabData ? <VisibilityOffIcon /> : <VisibilityIcon />
              }
              sx={{
                minWidth: "220px",
                minHeight: "64px",
                fontSize: "16px",
                fontWeight: "bold",
                padding: (theme) => theme.spacing(2, 3),
                borderRadius: (theme) => theme.shape.borderRadius,
                borderColor: (theme) =>
                  showSourceLabData
                    ? theme.palette.primary.main
                    : theme.palette.grey[400],
                backgroundColor: showSourceLabData
                  ? "transparent"
                  : (theme) => theme.palette.grey[50],
                color: (theme) =>
                  showSourceLabData
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
                borderWidth: 2,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderWidth: 2,
                  backgroundColor: (theme) =>
                    showSourceLabData
                      ? theme.palette.action.hover
                      : theme.palette.grey[100],
                },
                // Touch-friendly: larger touch target
                "@media (pointer: coarse)": {
                  minHeight: "72px",
                  fontSize: "18px",
                },
              }}
            >
              {showSourceLabData
                ? "Hide Tensile - Chemistry - Comments"
                : "Show Tensile - Chemistry - Comments"}
            </Button>
          )}
        </Box>

        {/* Close Button - positioned in the center */}
        <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
          <Button
            onClick={onHide}
            disabled={false} // Always allow closing the modal
            variant="contained"
            size="large"
            sx={{
              minWidth: "300px",
              minHeight: "64px",
              fontSize: "20px",
              fontWeight: "bold",
              padding: (theme) => theme.spacing(2, 6),
              borderRadius: (theme) => theme.shape.borderRadius,
              backgroundColor: (theme) => theme.palette.grey[100],
              color: (theme) => theme.palette.text.primary,
              border: (theme) => `1px solid ${theme.palette.grey[300]}`,
              boxShadow: "0 4px 14px 0 rgba(158, 158, 158, 0.25)",
              transition: "all 0.2s ease",
            }}
          >
            Close
          </Button>
        </Box>

        {/* Destination Tensile Toggle Button - right side */}
        <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
          {shouldShowLabDataSection && (
            <Button
              onClick={() => setShowDestinationLabData((prev) => !prev)}
              variant="outlined"
              size="large"
              startIcon={
                showDestinationLabData ? (
                  <VisibilityOffIcon />
                ) : (
                  <VisibilityIcon />
                )
              }
              sx={{
                minWidth: "220px",
                minHeight: "64px",
                fontSize: "16px",
                fontWeight: "bold",
                padding: (theme) => theme.spacing(2, 3),
                borderRadius: (theme) => theme.shape.borderRadius,
                borderColor: (theme) =>
                  showDestinationLabData
                    ? theme.palette.primary.main
                    : theme.palette.grey[400],
                backgroundColor: showDestinationLabData
                  ? "transparent"
                  : (theme) => theme.palette.grey[50],
                color: (theme) =>
                  showDestinationLabData
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
                borderWidth: 2,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderWidth: 2,
                  backgroundColor: (theme) =>
                    showDestinationLabData
                      ? theme.palette.action.hover
                      : theme.palette.grey[100],
                },
                // Touch-friendly: larger touch target
                "@media (pointer: coarse)": {
                  minHeight: "72px",
                  fontSize: "18px",
                },
              }}
            >
              {showDestinationLabData
                ? "Hide Tensile - Chemistry - Comments"
                : "Show Tensile - Chemistry - Comments"}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};
